import { Inject, Injectable, Optional } from '@angular/core';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
import {
  differenceInYears,
  differenceInMonths,
  differenceInDays,
  differenceInHours,
  differenceInMinutes
} from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { formatDateWithOptions } from '../helpers/format-date';
import { IDateFormatOptions } from '../models/date-time-format-options.model';

/**
 * Service for formatting dates with timezone and locale support. See library README for more details
 * and usage examples.
 */
@Injectable({ providedIn: 'root' })
export class LocalizedDateService {
  constructor(
    @Optional() @Inject(I18NEXT_SERVICE)
    private i18next: ITranslationService | null,
  ) {}

  /**
   * Gets browser's timezone
   */
  getBrowserTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  /**
   * Gets browser's locale
   */
  getBrowserLocale(): string {
    return navigator.language || 'en-US';
  }

  /**
   * Map country code to locale
   * @param country - Country code (e.g., 'US', 'GB', 'FR')
   * @returns Locale code (e.g., 'en-US', 'en-GB', 'fr-FR')
   */
  mapCountryToLocale(country: string): string {
    const countryToLocaleMap: Record<string, string> = {
      US: 'en-US',
      GB: 'en-GB',
      CA: 'en-CA',
      AU: 'en-AU',
      FR: 'fr-FR',
      DE: 'de-DE',
      ES: 'es-ES',
      IT: 'it-IT',
      JP: 'ja-JP',
      CN: 'zh-CN',
      MX: 'es-MX',
      BR: 'pt-BR',
      NL: 'nl-NL',
      SE: 'sv-SE',
      NO: 'nb-NO',
      DK: 'da-DK',
      FI: 'fi-FI',
      PL: 'pl-PL',
      PT: 'pt-PT',
      RU: 'ru-RU',
      IN: 'en-IN',
      KR: 'ko-KR'
    };

    return countryToLocaleMap[country.toUpperCase()] || 'en-US';
  }

  /**
   * Format date with timezone and locale support
   * @param date - Date to format (Date object or string)
   * @param options - Formatting options
   */
  formatDate(date: Date | string, options: IDateFormatOptions = {}): string {
    const {
      timezone = this.getBrowserTimezone(),
      locale = this.getBrowserLocale(),
      format = 'medium',
      customFormat
    } = options;

    // Convert to Date object if string
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) {
      return '';
    }

    return formatDateWithOptions(dateObj, format, locale, timezone, customFormat);
  }

  /**
   * Format date relative to now (e.g., "2 hours ago", "in 3 days")
   * @param date - Date to format
   * @param options - Formatting options
   */
  formatRelative(date: Date | string, options: Omit<IDateFormatOptions, 'format'> = {}): string {
    const {
      timezone = this.getBrowserTimezone(),
      locale = this.i18next?.language || this.getBrowserLocale()
    } = options;

    const dateObj = typeof date === 'string' ? new Date(date) : date;

    // Validate date
    if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) {
      return '';
    }

    // Convert both dates to the same timezone for comparison
    const zonedDate = toZonedTime(dateObj, timezone);
    const zonedNow = toZonedTime(new Date(), timezone);

    // Calculate differences using date-fns
    const diffYears = differenceInYears(zonedDate, zonedNow);
    const diffMonths = differenceInMonths(zonedDate, zonedNow);
    const diffDays = differenceInDays(zonedDate, zonedNow);
    const diffHours = differenceInHours(zonedDate, zonedNow);
    const diffMinutes = differenceInMinutes(zonedDate, zonedNow);

    // Create a relative time formatter
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

    // Determine which unit to use
    if (Math.abs(diffYears) >= 1) {
      return rtf.format(diffYears, 'year');
    }
    if (Math.abs(diffMonths) >= 1) {
      return rtf.format(diffMonths, 'month');
    }
    if (Math.abs(diffDays) >= 1) {
      return rtf.format(diffDays, 'day');
    }
    if (Math.abs(diffHours) >= 1) {
      return rtf.format(diffHours, 'hour');
    }
    return rtf.format(diffMinutes, 'minute');
  }
}
